import * as React from 'react';
import { DEFAULT_CLEAR_FILTERS_LABEL, DEFAULT_SEARCH_PLACEHOLDER } from '../../data-grid.constants';
import type {
	DataGridSelectedRowsMetadata,
	DataGridColumnDef,
	DataGridActionBarProps,
	DataGridFilterOptionsState,
	DataGridStrings,
} from '../../data-grid.type';
import { useSearchInput } from '../../hooks/use-search-input';
import { ColumnVisibilityMenu } from './column-visibility-menu';
import { FilterChipsContainer } from './filter-chip-container';
import { FilterMenu, type FilterOptionChange, type FilterState } from './filter-menu';
import { TableActions, type DataGridTableActions } from './table-actions';
import { resolveFilterLabels } from './util/resolve-filter-labels';
import { Input } from '@components/atoms';
import { Button } from '@components/molecules';

/**
 * Props for the ActionBar component
 * @interface ActionBarProps
 *
 * @property {any[]} [apiArgs] - Optional arguments passed to API calls
 *   Used when fetching data for filters or other dynamic content
 *
 * @property {DataGridColumnDef<CD>[]} columns - Column definitions for the data grid
 *   Defines the structure and behavior of grid columns including:
 *   - Filtering options
 *   - Visibility settings
 *   - Display configuration
 *
 * @property {FilterMenuProps<T, CD>['data']} data - Data used to populate filter options
 *   Source data for generating filter menus and options
 *
 * @property {Set<string>} [hiddenColumns] - Set of column keys that are currently hidden
 *   Used to track and manage column visibility state
 *
 * @property {(columnKey: string, isVisible: boolean) => void} [onColumnVisibilityChange] - Callback when column visibility changes
 *   Fired when:
 *   - User toggles column visibility
 *   - Column visibility is programmatically updated
 *
 * @property {(filters: Record<string, FilterState>) => void} [onFiltersChange] - Callback when active filters are modified
 *   Provides updated filter state when:
 *   - New filters are added
 *   - Existing filters are removed
 *   - Filter values are changed
 *
 * @property {(query: string) => void} [onSearch] - Callback when search query changes
 *   Fired on:
 *   - User input in search field
 *   - Search form submission
 *   Includes debounced handling to prevent excessive API calls
 *
 * @property {boolean} [showColumnVisibility] - Controls visibility of column management menu
 *   When true, displays interface for toggling column visibility
 *
 * @property {boolean} [showFilterMenu] - Controls visibility of filter menu
 *   When true, displays interface for managing data filters
 *
 * @property {boolean} [showSearch] - Controls visibility of search input
 *   When true, displays search interface
 *
 * @property {boolean} [showTableActions] - Controls visibility of table action buttons
 *   When true, displays configured table actions
 *
 * @property {DataGridTableActions['actions']} [tableActions] - Configuration for table-level actions
 *   Defines available actions that can be performed on the table data
 */
interface ActionBarProps<T> extends DataGridActionBarProps<T> {
	apiArgs?: any[];
	children?: React.ReactNode;
	columns: DataGridColumnDef<T>[];
	hiddenColumns?: Set<string>;
	onColumnVisibilityChange?: (columnKey: string, isVisible: boolean) => void;
	onFiltersChange: (update: FilterOptionChange | FilterOptionChange[]) => void;
	handleClearFilters: () => void;
	onSearch?: (query: string) => void;
	/** The grid's committed search term, so the box reflects a restored or navigated-to search. */
	searchQuery?: string;
	strings?: DataGridStrings;
	tableActions?: DataGridTableActions<T>['actions'];
	selectionMetadata: DataGridSelectedRowsMetadata<T>;
	filterStates?: DataGridFilterOptionsState;
	filterableColumns: DataGridColumnDef<T>[];
	selectedFilters: Record<string, FilterState>;
}

/**
 * A comprehensive action bar component for data grid interfaces with filtering, search, and column management
 *
 * @component
 * @example
 * // Basic usage with search and filters
 * <ActionBar
 *   columns={gridColumns}
 *   data={gridData}
 *   onSearch={handleSearch}
 *   showSearch={true}
 *   showFilterMenu={true}
 *   handleClearFilters={handleClearFilters}
 * />
 *
 * @example
 * // With column visibility management
 * <ActionBar
 *   columns={gridColumns}
 *   data={gridData}
 *   hiddenColumns={new Set(['column1', 'column2'])}
 *   handleClearFilters={handleClearFilters}
 *   onColumnVisibilityChange={(key, visible) => handleVisibility(key, visible)}
 *   showColumnVisibility={true}
 * />
 *
 * @example
 * // With table actions
 * <ActionBar
 *   columns={gridColumns}
 *   data={gridData}
 *   showTableActions={true}
 *   handleClearFilters={handleClearFilters}
 *   tableActions={[
 *     { label: 'Export', onClick: handleExport },
 *     { label: 'Delete Selected', onClick: handleDelete }
 *   ]}
 * />
 *
 * @example
 * // Full configuration with API integration
 * <ActionBar
 *   apiArgs={['param1', 'param2']}
 *   columns={gridColumns}
 *   data={gridData}
 *   hiddenColumns={hiddenColumns}
 *   onColumnVisibilityChange={handleVisibility}
 *   onFiltersChange={handleFilters}
 *   handleClearFilters={handleClearFilters}
 *   onSearch={handleSearch}
 *   showColumnVisibility={true}
 *   showFilterMenu={true}
 *   showSearch={true}
 *   showTableActions={true}
 *   tableActions={tableActionConfig}
 * />
 */
export function ActionBar<T>({
	apiArgs,
	// children,
	columns,
	hiddenColumns,
	onColumnVisibilityChange,
	onFiltersChange,
	handleClearFilters,
	onSearch,
	searchQuery,
	showColumnVisibility,
	showFilterGroupSearch,
	showFilterGroupSelectAll,
	showFilterMenu,
	showSearch,
	showTableActions,
	searchDisabled,
	searchWidth,
	strings,
	tableActions,
	selectionMetadata,
	filterStates,
	filterableColumns,
	selectedFilters,
	dateRangeFilter,
	hasCustomFilters,
	customActions,
	leadingSlot,
}: ActionBarProps<T>) {
	// A selection restored from the URL carries values but no labels, so the chips and menu would
	// render blank until the options it was built from have loaded.
	const labelledFilters = React.useMemo(
		() => resolveFilterLabels({ selectedFilters, filterStates }),
		[filterStates, selectedFilters]
	);
	const hasGridFilters = !!Object.keys(labelledFilters).length;
	const shouldShowClearFilters = (showFilterMenu && hasGridFilters) || hasCustomFilters;

	const {
		query,
		onChange: onSearchChange,
		onSubmit: onSearchSubmit,
	} = useSearchInput({
		onSearch,
		searchQuery,
	});

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onSearchChange(e.target.value);
	};

	const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		onSearchSubmit();
	};

	// Doubles as the input's accessible name; the search box has no visible label.
	const searchPlaceholder = strings?.actionBar?.searchPlaceholder ?? DEFAULT_SEARCH_PLACEHOLDER;

	const shouldShowColumnVisibility = showColumnVisibility && columns && hiddenColumns && onColumnVisibilityChange;

	return (
		<div data-aid="data-grid-action-bar" className="flex flex-col gap-2 pb-2">
			<div className="flex items-center justify-between gap-4">
				<div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
					{leadingSlot && (
						<div data-aid="data-grid-action-bar-leading" className="flex shrink-0 items-center">
							{leadingSlot}
						</div>
					)}
					{showSearch && onSearch && (
						<form
							data-aid="data-grid-action-bar-search"
							onSubmit={handleSearchSubmit}
							className="flex shrink-0 items-center sm:max-w-full"
						>
							<Input
								disabled={searchDisabled}
								aria-label={searchPlaceholder}
								onChange={handleSearchChange}
								placeholder={searchPlaceholder}
								type="text"
								value={query}
								variant="search"
								width={searchWidth}
							/>
						</form>
					)}

					{dateRangeFilter && (
						<div data-aid="data-grid-action-bar-date-range" className="flex shrink-0 items-center">
							{dateRangeFilter}
						</div>
					)}

					<div className="flex shrink-0 items-center gap-4">
						{showFilterMenu && (
							<FilterMenu
								apiArgs={apiArgs}
								onFilterChange={onFiltersChange}
								selectedFilters={labelledFilters}
								showFilterGroupSearch={showFilterGroupSearch}
								showFilterGroupSelectAll={showFilterGroupSelectAll}
								filterStates={filterStates}
								filterableColumns={filterableColumns}
							/>
						)}

						{shouldShowColumnVisibility && (
							<ColumnVisibilityMenu
								columns={columns}
								hiddenColumns={hiddenColumns}
								onVisibilityChange={onColumnVisibilityChange}
							/>
						)}

						{customActions && <div className="flex items-center gap-4">{customActions}</div>}

						{shouldShowClearFilters && (
							<Button onClick={handleClearFilters} variant="hyperlink" data-aid="data-grid-clear-filters-button">
								{DEFAULT_CLEAR_FILTERS_LABEL}
							</Button>
						)}
					</div>
				</div>

				{showTableActions && tableActions && (
					<TableActions<T> actions={tableActions} selectionMetadata={selectionMetadata} />
				)}
			</div>

			<FilterChipsContainer filters={labelledFilters} onRemoveFilter={onFiltersChange} />
		</div>
	);
}
